import {useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {createAccount, updateAccount, deleteAccount} from '../../api/capitalAccount';
import {findAllSources, createSource, updateSource, deleteSource} from '../../api/accountSource';
import {selectAccounts, selectAccountsInitialized, addAccount, updateAccountById, removeAccountById, removeAccountsBySourceId} from '../../store/accounts';
import {selectAccountSources, setAccountSources, addAccountSource, updateAccountSourceById, removeAccountSourceById} from '../../store/accountSources';
import {formatCurrency} from '../../utils';
import Loader from '../../components/common/Loader';
import Modal from '../../components/common/Modal';
import WalletSelect from '../../components/common/WalletSelect/WalletSelect';
import './AccountsPage.scss';

// ── Helpers ──────────────────────────────────────────────────────────

const todayStr = () => new Date().toISOString().split('T')[0];

const UNSOURCED = '__unsourced__';

const initSourceForm = () => ({name: '', notes: ''});
const sourceToForm = (s) => ({name: s.name ?? '', notes: s.notes ?? ''});

const initAccountForm = (sourceId) => ({
    sourceId: sourceId && sourceId !== UNSOURCED ? String(sourceId) : '',
    title: '',
    accountType: 'INVESTMENT',
    currencyCode: '',
    initialBalance: '0',
    openedDate: todayStr(),
    includeInStats: true,
    notes: '',
});

const accountToForm = (a) => ({
    sourceId: a.sourceId ? String(a.sourceId) : '',
    title: a.title ?? '',
    accountType: a.accountType ?? 'INVESTMENT',
    currencyCode: a.currencyCode ?? '',
    initialBalance: a.initialBalance != null ? String(a.initialBalance) : '0',
    openedDate: a.openedDate ?? todayStr(),
    includeInStats: a.includeInStats ?? true,
    notes: a.notes ?? '',
});

const buildAccountPayload = (f) => ({
    sourceId: f.sourceId ? Number(f.sourceId) : null,
    title: f.title,
    accountType: f.accountType,
    currencyCode: f.currencyCode,
    initialBalance: Number(f.initialBalance) || 0,
    openedDate: f.openedDate,
    includeInStats: f.includeInStats,
    notes: f.notes || null,
});

const ACCOUNT_TYPES = ['INVESTMENT', 'SAVINGS', 'CHECKING', 'CRYPTO', 'CASH', 'PENSION', 'OTHER'];
const ACCOUNT_TYPE_OPTIONS = ACCOUNT_TYPES.map(t => ({value: t, label: t}));

// ── Sub-components ───────────────────────────────────────────────────

const SourceForm = ({form, onChange, onSubmit, onCancel, saving, editId}) => {
    const set = (k, v) => onChange({...form, [k]: v});
    return (
        <form className="acct-form" onSubmit={e => { e.preventDefault(); onSubmit(); }}>
            <h3 className="acct-form__title">{editId ? 'Edit Source' : 'Add Source'}</h3>
            <div className="acct-form__field">
                <label className="acct-form__label">Name</label>
                <input className="acct-form__input" value={form.name} required
                       onChange={e => set('name', e.target.value)} placeholder="e.g. Monobank"/>
            </div>
            <div className="acct-form__field">
                <label className="acct-form__label">Notes <span className="acct-form__optional">(optional)</span></label>
                <textarea className="acct-form__input acct-form__textarea" value={form.notes}
                          onChange={e => set('notes', e.target.value)} rows={2}/>
            </div>
            <div className="acct-form__actions">
                <button type="button" className="acct-form__btn" onClick={onCancel}>Cancel</button>
                <button type="submit" className="acct-form__btn acct-form__btn--primary"
                        disabled={!form.name.trim() || saving}>
                    {saving ? 'Saving…' : editId ? 'Save' : 'Add Source'}
                </button>
            </div>
        </form>
    );
};

const AccountForm = ({form, onChange, onSubmit, onCancel, saving, editId, sources}) => {
    const set = (k, v) => onChange({...form, [k]: v});
    const canSubmit = form.title && form.accountType && form.currencyCode && form.openedDate;
    return (
        <form className="acct-form" onSubmit={e => { e.preventDefault(); if (canSubmit) onSubmit(); }}>
            <h3 className="acct-form__title">{editId ? 'Edit Account' : 'Add Account'}</h3>
            <div className="acct-form__row">
                <div className="acct-form__field">
                    <label className="acct-form__label">Title</label>
                    <input className="acct-form__input" value={form.title} required
                           onChange={e => set('title', e.target.value)} placeholder="e.g. Main savings"/>
                </div>
                <div className="acct-form__field">
                    <label className="acct-form__label">Type</label>
                    <WalletSelect
                        options={ACCOUNT_TYPE_OPTIONS}
                        value={ACCOUNT_TYPE_OPTIONS.find(o => o.value === form.accountType) ?? null}
                        onChange={opt => set('accountType', opt?.value ?? '')}
                        isClearable={false}
                    />
                </div>
            </div>
            <div className="acct-form__row">
                <div className="acct-form__field">
                    <label className="acct-form__label">Currency</label>
                    <input className="acct-form__input" placeholder="EUR" maxLength={3}
                           value={form.currencyCode}
                           onChange={e => set('currencyCode', e.target.value.toUpperCase())} required/>
                </div>
                <div className="acct-form__field">
                    <label className="acct-form__label">Initial Balance</label>
                    <input className="acct-form__input" type="number" step="0.01"
                           value={form.initialBalance}
                           onChange={e => set('initialBalance', e.target.value)}/>
                </div>
            </div>
            <div className="acct-form__row">
                <div className="acct-form__field">
                    <label className="acct-form__label">Opened Date</label>
                    <input className="acct-form__input" type="date" value={form.openedDate} required
                           onChange={e => set('openedDate', e.target.value)}/>
                </div>
                <div className="acct-form__field">
                    <label className="acct-form__label">Source <span className="acct-form__optional">(optional)</span></label>
                    <WalletSelect
                        options={sources.map(s => ({value: String(s.id), label: s.name}))}
                        value={form.sourceId ? {value: String(form.sourceId), label: sources.find(s => String(s.id) === String(form.sourceId))?.name ?? ''} : null}
                        onChange={opt => set('sourceId', opt?.value ?? '')}
                        placeholder="— none —"
                        isClearable={true}
                    />
                </div>
            </div>
            <label className="acct-form__checkbox-row">
                <input type="checkbox" checked={form.includeInStats}
                       onChange={e => set('includeInStats', e.target.checked)}/>
                Include in stats
            </label>
            <div className="acct-form__field">
                <label className="acct-form__label">Notes <span className="acct-form__optional">(optional)</span></label>
                <textarea className="acct-form__input acct-form__textarea" value={form.notes}
                          onChange={e => set('notes', e.target.value)} rows={2}/>
            </div>
            <div className="acct-form__actions">
                <button type="button" className="acct-form__btn" onClick={onCancel}>Cancel</button>
                <button type="submit" className="acct-form__btn acct-form__btn--primary"
                        disabled={!canSubmit || saving}>
                    {saving ? 'Saving…' : editId ? 'Save' : 'Add Account'}
                </button>
            </div>
        </form>
    );
};

// ── Main ─────────────────────────────────────────────────────────────

const AccountsPage = () => {
    const dispatch = useDispatch();
    const accounts = useSelector(selectAccounts);
    const accountsInitialized = useSelector(selectAccountsInitialized);
    const sources = useSelector(selectAccountSources);
    const [selectedId, setSelectedId] = useState(null);
    const [modal, setModal] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [sourceForm, setSourceForm] = useState(initSourceForm());
    const [accountForm, setAccountForm] = useState(initAccountForm(null));
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [mobileView, setMobileView] = useState('list');

    // ── Grouping ────────────────────────────────────────────────────
    const unsourced = useMemo(() => accounts.filter(a => !a.sourceId), [accounts]);
    const bySource = useMemo(() => {
        const map = {};
        for (const a of accounts) {
            if (a.sourceId) {
                if (!map[a.sourceId]) map[a.sourceId] = [];
                map[a.sourceId].push(a);
            }
        }
        return map;
    }, [accounts]);

    const visibleAccounts = useMemo(() => {
        if (!selectedId) return [];
        if (selectedId === UNSOURCED) return unsourced;
        return bySource[selectedId] ?? [];
    }, [selectedId, unsourced, bySource]);

    const selectedSource = useMemo(
        () => sources.find(s => s.id === selectedId),
        [sources, selectedId],
    );

    // ── Actions ─────────────────────────────────────────────────────
    const openCreateSource = () => {
        setEditTarget(null);
        setSourceForm(initSourceForm());
        setModal('source');
    };
    const openEditSource = (src, e) => {
        e.stopPropagation();
        setEditTarget(src);
        setSourceForm(sourceToForm(src));
        setModal('source');
    };
    const openCreateAccount = () => {
        setEditTarget(null);
        setAccountForm(initAccountForm(selectedId));
        setModal('account');
    };
    const openEditAccount = (acct) => {
        setEditTarget(acct);
        setAccountForm(accountToForm(acct));
        setModal('account');
    };
    const closeModal = () => { setModal(null); setEditTarget(null); };

    const handleSaveSource = async () => {
        setSaving(true);
        try {
            const payload = {name: sourceForm.name.trim(), notes: sourceForm.notes || null};
            if (editTarget) {
                const saved = await updateSource(editTarget.id, payload);
                dispatch(updateAccountSourceById(saved));
            } else {
                const saved = await createSource(payload);
                dispatch(addAccountSource(saved));
            }
            closeModal();
        } finally { setSaving(false); }
    };

    const syncSources = () =>
        findAllSources().then(data => dispatch(setAccountSources(data)));

    const handleSaveAccount = async () => {
        setSaving(true);
        try {
            const payload = buildAccountPayload(accountForm);
            if (editTarget) {
                const saved = await updateAccount(editTarget.id, payload);
                dispatch(updateAccountById(saved));
            } else {
                const saved = await createAccount(payload);
                dispatch(addAccount(saved));
            }
            closeModal();
            syncSources();
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const {id, kind} = deleteConfirm;
        setDeleteConfirm(null);
        if (kind === 'account') {
            await deleteAccount(id);
            dispatch(removeAccountById(id));
            syncSources();
        } else {
            await deleteSource(id);
            dispatch(removeAccountSourceById(id));
            dispatch(removeAccountsBySourceId(id));
            if (selectedId === id) { setSelectedId(null); setMobileView('list'); }
        }
    };

    // ── Render ───────────────────────────────────────────────────────
    return (
        <div className="capital-page">
            <div className="capital-page__header">
                <h2 className="capital-page__title">Accounts</h2>
                <div className="accounts-actions">
                    <button className="wallet-add-button" onClick={openCreateSource}>+ Source</button>
                    <button className="wallet-add-button" onClick={openCreateAccount}>+ Account</button>
                </div>
            </div>

            {!accountsInitialized ? <Loader/> : <div className={`capital-page__body accounts-layout${mobileView === 'detail' ? ' is-showing-detail' : ''}`}>
                {/* Left — source list */}
                <div className="accounts-list">
                    {sources.map(src => (
                        <div key={src.id}
                             className={`src-item${selectedId === src.id ? ' is-active' : ''}`}
                             onClick={() => { setSelectedId(src.id); setMobileView('detail'); }}>
                            <span className="src-item__name">{src.name}</span>
                            <span className="src-item__count">{bySource[src.id]?.length ?? 0}</span>
                        </div>
                    ))}
                    {unsourced.length > 0 && (
                        <div className={`src-item src-item--unsourced${selectedId === UNSOURCED ? ' is-active' : ''}`}
                             onClick={() => { setSelectedId(UNSOURCED); setMobileView('detail'); }}>
                            <span className="src-item__name">Unsourced</span>
                            <span className="src-item__count">{unsourced.length}</span>
                        </div>
                    )}
                    {sources.length === 0 && unsourced.length === 0 && (
                        <p className="accounts-empty">No accounts yet.</p>
                    )}
                </div>

                {/* Right — detail */}
                <div className="accounts-detail">
                    <button className="mobile-back-btn" onClick={() => setMobileView('list')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M19 12H5M12 5l-7 7 7 7"/>
                        </svg> Sources
                    </button>
                    {!selectedId ? (
                        <div className="accounts-detail__placeholder">
                            Select a source to view accounts
                        </div>
                    ) : (
                        <div className="source-detail">
                            <div className="source-detail__header">
                                <h3 className="source-detail__title">
                                    {selectedId === UNSOURCED ? 'Unsourced accounts' : selectedSource?.name}
                                </h3>
                                {selectedSource && (<>
                                    <button className="detail-btn"
                                            onClick={(e) => openEditSource(selectedSource, e)}>
                                        Edit
                                    </button>
                                    <button className="detail-btn detail-btn--danger"
                                            onClick={() => setDeleteConfirm({id: selectedSource.id, kind: 'source'})}>
                                        Delete
                                    </button>
                                </>)}
                            </div>

                            {selectedSource?.notes && (
                                <p className="source-detail__notes">{selectedSource.notes}</p>
                            )}

                            <div className="source-detail__section">
                                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                    <p className="source-detail__subtitle">
                                        Accounts ({visibleAccounts.length})
                                    </p>
                                    <button className="detail-btn" style={{marginLeft: 'auto'}}
                                            onClick={openCreateAccount}>
                                        + Add Account
                                    </button>
                                </div>
                                {visibleAccounts.length === 0 ? (
                                    <p className="accounts-empty">No accounts in this group.</p>
                                ) : visibleAccounts.map(acct => {
                                    const bal = acct.balance;
                                    return (
                                        <div key={acct.id} className="account-row"
                                             onClick={() => openEditAccount(acct)}>
                                            <div className="account-row__info">
                                                <div className="account-row__title">{acct.title}</div>
                                                <div className="account-row__meta">
                                                    {acct.accountType} · {acct.currencyCode}
                                                    {!acct.includeInStats && ' · excluded from stats'}
                                                </div>
                                            </div>
                                            <div className={`account-row__balance ${bal >= 0 ? 'positive-value' : 'negative-value'}`}>
                                                {bal != null ? formatCurrency(bal, acct.currencyCode) : '—'}
                                            </div>
                                            <button className="btn-del"
                                                    title="Delete"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setDeleteConfirm({id: acct.id, kind: 'account'});
                                                    }}>
                                                ×
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>}

            {/* Source modal */}

            {modal === 'source' && (
                <Modal onClose={closeModal}>
                    <SourceForm form={sourceForm} onChange={setSourceForm}
                                onSubmit={handleSaveSource} onCancel={closeModal}
                                saving={saving} editId={editTarget?.id}/>
                </Modal>
            )}

            {/* Account modal */}
            {modal === 'account' && (
                <Modal onClose={closeModal}>
                    <AccountForm form={accountForm} onChange={setAccountForm}
                                 onSubmit={handleSaveAccount} onCancel={closeModal}
                                 saving={saving} editId={editTarget?.id} sources={sources}/>
                </Modal>
            )}

            {/* Delete confirm */}
            {deleteConfirm && (
                <Modal onClose={() => setDeleteConfirm(null)}>
                    <div className="acct-form">
                        <h3 className="acct-form__title">
                            Delete {deleteConfirm.kind === 'account' ? 'account' : 'source'}?
                        </h3>
                        <p style={{margin: 0, fontSize: 13, color: 'var(--text-muted)'}}>
                            {deleteConfirm.kind === 'source'
                                ? 'Accounts in this source will become unsourced.'
                                : 'This cannot be undone.'}
                        </p>
                        <div className="acct-form__actions">
                            <button className="acct-form__btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                            <button className="acct-form__btn acct-form__btn--danger" onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default AccountsPage;
